import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Button, Dropdown, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useStoreGroups } from "../../hooks/useStoreGroups";
import "./storeManagement.scss";

const StoreGroupModal = ({
  isOpen,
  onClose,
  onSave,
  onEditGroup,
  onGroupCreated,
  preSelectedStoreIds = [],
  defaultMode = "existing",
}) => {
  const [mode, setMode] = useState(defaultMode);
  const [groupName, setGroupName] = useState("");
  const [selectedExistingGroup, setSelectedExistingGroup] = useState([]);
  const [modalToast, setModalToast] = useState([]);

  useEffect(() => {
    if (isOpen) setMode(defaultMode);
  }, [isOpen, defaultMode]);

  const {
    storeGroups: storeGroupOptions,
    savingTag,
    createStoreGroup,
  } = useStoreGroups();

  const handleClose = useCallback(() => {
    setMode(defaultMode);
    setGroupName("");
    setSelectedExistingGroup([]);
    setModalToast([]);
    onClose();
  }, [onClose, defaultMode]);

  const handleNext = useCallback(async () => {
    if (mode === "existing") {
      const selected = selectedExistingGroup?.[0]?.id;
      if (!selected) {
        setModalToast([
          { text: "Please select a store group", type: "error", id: "select-error" },
        ]);
        return;
      }
      if (preSelectedStoreIds.length > 0) {
        onSave(selected, preSelectedStoreIds);
      } else if (onEditGroup) {
        onEditGroup(selected);
      }
      handleClose();
    } else {
      const name = groupName.trim();
      if (!name) {
        setModalToast([
          { text: "Please enter a group name", type: "error", id: "name-error" },
        ]);
        return;
      }

      const isDuplicate = storeGroupOptions.some(
        (g) => g.id.toLowerCase() === name.toLowerCase()
      );
      if (isDuplicate) {
        setModalToast([
          {
            text: "A group with this name already exists",
            type: "error",
            id: "dup-error",
          },
        ]);
        return;
      }

      try {
        await createStoreGroup(name);
        if (preSelectedStoreIds.length > 0) {
          onSave(name, preSelectedStoreIds);
        }
        if (onGroupCreated) onGroupCreated();
        handleClose();
      } catch {
        setModalToast([
          {
            text: "Failed to create store group. Try again.",
            type: "error",
            id: "save-error",
          },
        ]);
      }
    }
  }, [
    mode,
    groupName,
    selectedExistingGroup,
    preSelectedStoreIds,
    storeGroupOptions,
    onSave,
    onEditGroup,
    createStoreGroup,
    handleClose,
    onGroupCreated,
  ]);

  if (!isOpen) return null;

  return (
    <Dialog
      className="store-group-modal"
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <h3 style={{ margin: 0 }}>Add Group</h3>
          </Grid>
          <Grid item>
            <CloseIcon
              onClick={handleClose}
              className="cursor-pointer"
              data-testid="store-group-modal-close"
            />
          </Grid>
        </Grid>
        <ToastContainer
          data={modalToast}
          removeToast={() => setModalToast([])}
          showActionIcon
        />
      </DialogTitle>
      <DialogContent>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="existing"
            control={<Radio size="small" />}
            label="Select existing group"
          />
          {mode === "existing" && (
            <div className="store-group-field">
              <Dropdown
                childList={storeGroupOptions}
                selection="single"
                selectedValue={selectedExistingGroup}
                setSelectedValue={setSelectedExistingGroup}
                placeholder="Select"
                size="medium"
              />
            </div>
          )}

          <FormControlLabel
            value="new"
            control={<Radio size="small" />}
            label="Create new group"
          />
          {mode === "new" && (
            <div className="store-group-field">
              <TextField
                size="small"
                fullWidth
                placeholder="Type group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                inputProps={{ maxLength: 20 }}
              />
            </div>
          )}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={handleClose}
          text="Cancel"
          size="small"
        />
        <Button
          variant="primary"
          onClick={handleNext}
          text="Next"
          size="small"
          disabled={savingTag}
        />
      </DialogActions>
    </Dialog>
  );
};

StoreGroupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onEditGroup: PropTypes.func,
  onGroupCreated: PropTypes.func,
  preSelectedStoreIds: PropTypes.array,
};

export default StoreGroupModal;
